/**
 * THE GUIDE, START TO FINISH. This is the only file you need to edit.
 *
 * Reword a step, reorder the array, or delete an entry — nothing outside this
 * file knows what the steps are or how many there are. The step counter
 * ("3 of 21") is derived from the array length, so removing a step renumbers the
 * rest automatically. Nothing here is a translation key; the strings are the
 * strings.
 *
 * All body text below is PLACEHOLDER, written to be replaced.
 *
 * Adding a step needs two things:
 *   1. an entry here;
 *   2. a `data-tour="<anchor>"` attribute on the element it points at.
 *
 * If an anchor is not on screen when its step comes up, the guide waits
 * (ANCHOR_TIMEOUT_MS) and then shows the step ANYWAY, centred and without a
 * spotlight — and keeps watching, so an anchor that arrives late still gets
 * spotlit rather than being written off. A step whose target never appears (an
 * empty swipe deck) simply stays un-spotlit. Nothing auto-advances: the reader
 * sets the pace throughout.
 *
 * ── Surfaces ────────────────────────────────────────────────────────────────
 * A step can require a modal or a sub-screen to be open. It DECLARES the state
 * it needs rather than listing open/close actions, and the guide reconciles to
 * that on every step change.
 *
 * That is deliberate, and it is what makes Back work. With onEnter/onExit pairs,
 * going backwards has to run the inverse of a transition that already ran, and
 * every route in and out of a step needs its own handling — Back from step 5
 * would have to know to reopen the post modal. Because a step instead says only
 * "I need the post modal open", moving in ANY direction is one operation: look
 * at what is open, look at what this step wants, close or open the difference.
 * Skip and finish are that same operation with a target of nothing.
 */

import { PATHS } from '../router/paths';

/**
 * How long to wait for an anchor before giving up on the SPOTLIGHT for a step.
 * The step itself still shows, centred and without a cut-out, and still waits
 * for Next — it is the highlight that is dropped, never the step.
 *
 * Five seconds because the screens this has to cover fetch their content first:
 * Explore loads tribes and forums, Chat loads rooms and conversations, Profile
 * loads the account. Two seconds ran out on all of them on a cold start.
 */
export const ANCHOR_TIMEOUT_MS = 5000;

/**
 * A modal or sub-screen a step needs open.
 *
 * Absent means the plain screen with nothing over it, which is what every step
 * that does not name a surface gets.
 */
export type GuideSurface =
  | 'postModal'      // the Create Yap composer
  | 'swipe'          // the full-screen Discover swipe deck
  | 'chatRooms'      // Chat, Rooms section
  | 'chatMessages'   // Chat, Direct Messages section
  | 'createRoom'     // the Create Room modal, over the Rooms section
  | 'shareProfile';  // the Share Profile modal

export interface GuideStep {
  /** Stable id. Used for logging and as the React key; never shown to the user. */
  id: string;
  /** Route to be on for this step. The guide navigates here first. */
  route: string;
  /**
   * Which tab to land on, for screens that have them. Passed through router
   * state exactly as the app's own navigation does.
   */
  tab?: 'world' | 'friends';
  /** A modal or sub-screen this step needs open. Omitted means none. */
  surface?: GuideSurface;
  /**
   * Leave `surface` open when the tour ENDS on this step.
   *
   * Only the closing step uses it: the tour finishes with the composer open and
   * ready to type into, so finishing must not tidy it away the way every other
   * exit does.
   */
  keepSurfaceOnFinish?: boolean;
  /**
   * The `data-tour` value of the element to spotlight.
   * `null` means no target: the bubble is centred and nothing is cut out.
   */
  anchor: string | null;
  title: string;
  body: string;
  /**
   * Where the bubble sits relative to the spotlight. Defaults to 'auto', which
   * puts it below the anchor when there is room and above it otherwise.
   */
  placement?: 'auto' | 'above' | 'below';
}

export const GUIDE_STEPS: GuideStep[] = [
  // ── YapTab ────────────────────────────────────────────────────────────────
  {
    id: 'yaptab-composer',
    route: PATHS.YAP_TAB,
    tab: 'world',
    anchor: 'guide-composer',
    title: 'Post a Yap',
    body: "Share what's on your mind with the whole community.",
  },
  {
    id: 'post-topics',
    route: PATHS.YAP_TAB,
    tab: 'world',
    surface: 'postModal',
    anchor: 'guide-post-topics',
    title: 'Topics',
    body: 'Tag your yap with a topic so the right people find it.',
  },
  {
    id: 'post-polls',
    route: PATHS.YAP_TAB,
    tab: 'world',
    surface: 'postModal',
    anchor: 'guide-post-polls',
    title: 'Polls',
    body: 'Ask the community a question and let them vote.',
  },
  {
    id: 'post-anonymous',
    route: PATHS.YAP_TAB,
    tab: 'world',
    surface: 'postModal',
    anchor: 'guide-post-anonymous',
    title: 'Anonymous',
    body: 'Post without your name attached when you want to speak freely.',
  },

  // ── Discover ──────────────────────────────────────────────────────────────
  {
    id: 'discover-swipe-button',
    route: PATHS.DISCOVER,
    anchor: 'guide-discover',
    title: 'Find your people',
    body: 'Press this lightning button to see everyone on the app! You can swipe through them based on their interests',
  },
  {
    id: 'swipe-add',
    route: PATHS.DISCOVER,
    surface: 'swipe',
    anchor: 'guide-swipe-add',
    title: 'Add',
    body: "Do you think you'd be friends with this user based on their interests? Add them and find out!",
  },
  {
    id: 'swipe-reject',
    route: PATHS.DISCOVER,
    surface: 'swipe',
    anchor: 'guide-swipe-reject',
    title: 'Reject',
    body: "You don't think you guys would vibe? Skip them and move onto the next person!",
  },
  {
    id: 'swipe-filter',
    route: PATHS.DISCOVER,
    surface: 'swipe',
    anchor: 'guide-swipe-filter',
    title: 'Filters',
    body: 'Narrow people down by interests, location, and more.',
  },
  {
    id: 'swipe-back',
    route: PATHS.DISCOVER,
    surface: 'swipe',
    anchor: 'guide-swipe-back',
    title: 'Back',
    body: 'Head back to Discover anytime.',
  },
  {
    id: 'discover-trending',
    route: PATHS.DISCOVER,
    anchor: 'guide-trending',
    title: 'Trending Yaps',
    body: "The yaps everyone's talking about right now.",
  },
  {
    id: 'discover-similar',
    route: PATHS.DISCOVER,
    anchor: 'guide-similar',
    title: 'Similar to Me',
    body: 'People who share your roots and interests.',
  },
  {
    id: 'discover-groups',
    route: PATHS.DISCOVER,
    anchor: 'guide-groups',
    title: 'Groups',
    body: 'Updates from the tribes and forums on the app.',
  },
  {
    id: 'discover-newest',
    route: PATHS.DISCOVER,
    anchor: 'guide-newest',
    title: 'Newest Users',
    body: 'Say hi to the newest members of the community.',
  },

  // ── Explore ───────────────────────────────────────────────────────────────
  {
    id: 'explore-tribes',
    route: PATHS.EXPLORE,
    anchor: 'guide-tribes',
    title: 'Tribes',
    body: 'Tribes are communities built around a place, a school, or an occupation. Join the ones that fit.',
  },
  {
    id: 'explore-forums',
    route: PATHS.EXPLORE,
    anchor: 'guide-forums',
    title: 'Forums',
    body: 'Forums are for topics rather than identity: music, business, football, whatever you are into.',
  },

  // ── Chat ──────────────────────────────────────────────────────────────────
  {
    id: 'chat-rooms',
    route: PATHS.CHAT,
    surface: 'chatRooms',
    anchor: 'guide-rooms-list',
    title: 'Rooms',
    body: 'Rooms are live group chatrooms you can join on any topic.',
  },
  {
    id: 'chat-create-room',
    route: PATHS.CHAT,
    // Opens the modal rather than only pointing at the button: the thing worth
    // seeing is what starting a room actually asks for.
    surface: 'createRoom',
    anchor: 'guide-create-room-modal',
    title: 'Making a room',
    body: 'Start your own timed chatroom and invite people in!',
  },
  {
    id: 'chat-dms',
    route: PATHS.CHAT,
    surface: 'chatMessages',
    anchor: 'guide-chats',
    title: 'Direct messages',
    body: 'Your private conversations live here.',
  },

  // ── Profile ───────────────────────────────────────────────────────────────
  {
    id: 'profile',
    route: PATHS.PROFILE,
    anchor: 'guide-profile-block',
    title: 'Your profile',
    body: 'This is you. Edit your profile or share it anytime.',
  },
  {
    id: 'profile-share',
    route: PATHS.PROFILE,
    surface: 'shareProfile',
    anchor: 'guide-share-card',
    title: 'Share your profile',
    body: 'Share your AfroYap profile card anywhere.',
  },

  // ── Finish ────────────────────────────────────────────────────────────────
  {
    // Ends with the composer OPEN and usable. keepSurfaceOnFinish is what stops
    // the tour tidying it away on the way out, which is what every other exit
    // does — the last thing it says is "now make your first yap", so the thing
    // it is asking for has to still be there when the overlay goes.
    id: 'done',
    route: PATHS.YAP_TAB,
    tab: 'world',
    surface: 'postModal',
    keepSurfaceOnFinish: true,
    // The composer card itself, so the last thing the tour says is pointing at
    // the thing it is asking for rather than floating over it.
    anchor: 'guide-post-modal',
    title: "You're all set",
    body: 'Now make your first yap!',
  },
];
